/**
 * Garage colours on the live models, without editing src/view:
 * - bike plastics: the textured "Orange_Plastics" material of bike.glb gets a colour node that re-tints its orange
 *   areas from a uniform (graphics, whites and blacks stay), so a livery change is a uniform update;
 * - rider kit: RiderView.setKit (src/view/riderView.ts), reached through the view's rider when present.
 */
import * as THREE from 'three/webgpu';
import { clamp, dot, max, min, mix, smoothstep, step, texture, uniform, uv, vec3 } from 'three/tsl';
import type { PrettyView } from '../view/prettyView';
import { Helmet, Kit, Livery } from '../ui/logic/livery';
import { jerseyArt } from '../view/world/schoolKit';

const LUMA = vec3(0.2126, 0.7152, 0.0722);
// TSL node types do not compose across float/vec3 helpers in @types/three; the graph is checked when it compiles.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** Mask of saturated warm (orange/red/yellow) areas of a colour. */
function warmMask(c: N): N {
  const hi = max(c.x, max(c.y, c.z)), lo = min(c.x, min(c.y, c.z));
  return smoothstep(0.12, 0.3, hi.sub(lo)).mul(step(c.z, c.x));
}

type KitSetter = {
  setKit?: (k: Record<string, THREE.ColorRepresentation>) => void;
  setJerseyArt?: (art: HTMLCanvasElement | null) => void;
  setGoggles?: (c: THREE.ColorRepresentation) => void;
  landmarks?: { hip: number; shoulder: number };
};

export class LiveryPainter {
  private readonly bikePrimary = uniform(new THREE.Color(0xff6a00));
  private readonly done = new WeakSet<THREE.Material>();
  bikeParts = 0;

  constructor(private readonly view: PrettyView) {
    // The school jersey carries the logo; until it loads the artwork uses a drawn emblem, then it is reprinted.
    const img = new Image();
    img.onload = () => { this.logo = img; this.art = null; this.printed?.(); };
    img.src = `${import.meta.env.BASE_URL}brand/megamotocross_logo_c.svg`;
  }

  /** Converts the plastics under `root` (idempotent). */
  attach(root: THREE.Object3D): void {
    root.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      for (const m of (Array.isArray(o.material) ? o.material : [o.material]) as THREE.MeshStandardNodeMaterial[]) {
        if (this.done.has(m) || !/plastic/i.test(m.name) || !m.map) continue;
        const c: N = texture(m.map, uv()).rgb;
        const tinted = (this.bikePrimary as N).mul(clamp(dot(c, LUMA).div(0.316), 0, 2.5));
        m.colorNode = mix(c, tinted, warmMask(c));
        m.needsUpdate = true;
        this.done.add(m);
        this.bikeParts++;
      }
    });
  }

  private art: HTMLCanvasElement | null = null;
  private logo: HTMLImageElement | null = null;
  private printed: (() => void) | null = null;

  set(bike: Livery, kit: Kit, helmet?: Helmet): void {
    (this.bikePrimary.value as THREE.Color).setHex(bike.primary);
    // The view keeps its rider private; setKit is its public colour API (riderView.ts).
    const rider = (this.view as unknown as { rider?: KitSetter }).rider;
    rider?.setKit?.(helmet ? { ...kit.kit, helmet: helmet.primary, helmetAccent: helmet.secondary } : kit.kit);
    if (helmet) rider?.setGoggles?.(helmet.goggles);
    this.printed = () => {
      if (kit.art === 'school' && rider?.landmarks) this.art ??= jerseyArt(rider.landmarks.hip, rider.landmarks.shoulder, this.logo);
      rider?.setJerseyArt?.(kit.art === 'school' ? this.art : null);
    };
    this.printed();
  }
}
